import bannerPlaceholder from "assets/images/evento-banner.jpg";
import { CameraOn, Close, Trash, CloudUpload } from "assets/svg";
import BaseModal from "components/BaseModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventStore } from "store/useEventStore";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import logoRedIcon from "../../assets/images/logo-rose-icon.png";
import logoBlackIcon from "../../assets/images/logo-black-icon.png";
import { useGuestStore } from "store";
import { clarity } from "react-microsoft-clarity";
import * as SpacesService from "libs/digitalocean/SpacesService";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import {
  TBingoAchievements,
  TBingoChallenge,
  TBingoChallengeDone,
  TBingoResult,
} from "services/types";
import TopBar from "pages/FeedV2/TopBar";
import { confettiShoot } from "./gamesUtils";
import GuestFormModal from "components/GuestFormModal";
import CreatePost from "pages/FeedV2/CreatePost";
import { useFeedStore } from "store";
import { TFeedEvent } from "cdt-app-public-api/dist/types";

const BINGO_CONFIG = {
  rows: [
    [1, 2, 3, 4, 5],
    [6, 7, 8, 9, 10],
    [11, 12, 14, 15], // Note: 13 is the center (logo)
    [16, 17, 18, 19, 20],
    [21, 22, 23, 24, 25],
  ],
  cols: [
    [1, 6, 11, 16, 21],
    [2, 7, 12, 17, 22],
    [3, 8, 18, 23], // Note:  13 is the center (logo)
    [4, 9, 14, 19, 24],
    [5, 10, 15, 20, 25],
  ],
  allNumbers: [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22,
    23, 24, 25,
  ],
};

const CENTER_CHALLENGE_ID = 13;

export default function Bingo() {
  const { theme, event } = useEventStore();
  const { guest, setGameStatus } = useGuestStore();
  const navigate = useNavigate();
  const [step, setStep] = useState<"intro" | "game">("intro");
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [shouldStartAfterModal, setShouldStartAfterModal] = useState(false);

  const onBingoClose = () => {
    if (event?.betaFeatures?.includes("feed")) {
      return navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("game_bingo", "engaged");
    }
  }, []);

  const onStartClick = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (!guest?.name) {
      setGuestModalOpen(true);
      setShouldStartAfterModal(true);
      return;
    }

    setGameStatus("bingo_intro_seen", true);
    setStep("game");
  }, [event?.id, guest?.id, guest?.name, setGameStatus]);

  const handleGuestModalClose = useCallback(() => {
    setGuestModalOpen(false);

    if (shouldStartAfterModal) {
      setShouldStartAfterModal(false);
      setGameStatus("bingo_intro_seen", true);
      setStep("game");
    }
  }, [shouldStartAfterModal, setGameStatus]);

  return (
    <div
      className={`${theme} w-full flex justify-center items-center px-5 py-16`}
    >
      <div className="w-full flex flex-col justify-start items-center">
        {/* Header */}
        <div className="flex w-full items-center justify-between mb-4">
          <h1 className="m-0 p-0 grow text-center">Bingo</h1>

          {event?.betaFeatures?.includes("feed") && (
            <TopBar onClose={onBingoClose} />
          )}
          {step !== "intro" && !event?.betaFeatures?.includes("feed") && (
            <button
              className="flex items-center text-primary fixed right-3 top-3"
              onClick={onBingoClose}
            >
              <span className="text-lg">
                <Close width={36} height={36} color="none" outline="#414141" />
              </span>
            </button>
          )}
        </div>
        {step === "intro" && <Intro onStartClick={onStartClick} />}
        {step === "game" && <Game />}
      </div>
      <GuestFormModal isOpen={guestModalOpen} onClose={handleGuestModalClose} />
    </div>
  );
}

const Intro = ({ onStartClick }: { onStartClick: () => void }) => {
  const { theme, event } = useEventStore();
  const [countdown, setCountdown] = useState(3);
  const [isDisabled, setIsDisabled] = useState(true);
  const {
    getGameStatus,
    games: { bingoChallengesDone },
  } = useGuestStore();

  const hasSeenIntro = getGameStatus("bingo_intro_seen");
  const hasProgress = bingoChallengesDone.length > 0;

  useEffect(() => {
    if (hasSeenIntro) {
      setIsDisabled(false);
      setCountdown(0);
      return;
    }

    if (countdown === 0) {
      setIsDisabled(false);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, hasSeenIntro]);

  return (
    <div className="flex flex-col justify-center items-center gap-4">
      {event?.customization?.theme.name === "rose" ? (
        <div className="relative wedding-rose">
          <img
            className="aspect-square object-cover w-full h-full max-w-full"
            src={event?.bannerUrl || bannerPlaceholder}
            alt="banner-home"
          />
        </div>
      ) : (
        <div className="relative bg-white p-2 shadow-md">
          <img
            src={event?.bannerUrl || bannerPlaceholder}
            alt="event banner"
            className="aspect-[1/1.15] object-cover max-w-full"
          />
        </div>
      )}
      {hasProgress ? (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.bingo.progress"
            defaultMessage="Continue completando os desafios do bingo!"
          />
        </p>
      ) : (
        <p className="text-lg text-gray-500">
          <FormattedMessage
            id="message.bingo.intro"
            defaultMessage="Consegue completar o bingo se divertindo em cada desafio?"
          />
        </p>
      )}
      <div className="w-full flex flex-col justify-center items-center gap-8">
        <button
          onClick={onStartClick}
          className={`px-10 py-3 ${theme.button}`}
          disabled={isDisabled}
        >
          <span className="text-center w-full">
            {hasProgress ? (
              <FormattedMessage
                id="button.bingo.continue"
                defaultMessage="Continuar"
              />
            ) : (
              <FormattedMessage
                id="button.bingo.start"
                defaultMessage="Iniciar"
              />
            )}
            {isDisabled && (
              <span className=" ml-2 animate-pulse">{countdown}</span>
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

const Game = () => {
  const { theme, event } = useEventStore();
  const {
    guest,
    games: { bingoChallengesDone, bingoAchievements },
    addBingoChallengeDone,
    updateBingoChallengeDone,
    removeBingoChallengeDone,
    setBingoAchievements,
    getGameStatus,
    setGameStatus,
  } = useGuestStore();

  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [takenPhotos, setTakenPhotos] = useState<{ [key: number]: string }>({});
  const [completedChallenges, setCompletedChallenges] = useState<
    Record<number, TBingoChallengeDone>
  >({});

  const [showRemoveAlert, setShowRemoveAlert] = useState<boolean>(false);

  const bingoData = useMemo(() => {
    if (!event?.bingo) return {};

    const challenges = event.bingo as TBingoChallenge[];
    const centerChallenge = challenges.find(
      (challenge) => challenge.text === "LOVE"
    );
    const otherChallenges = challenges.filter(
      (challenge) => challenge.text !== "LOVE"
    );

    const data: Record<number, { id: number; text: string }> = {};

    if (centerChallenge) {
      data[CENTER_CHALLENGE_ID] = {
        id: CENTER_CHALLENGE_ID,
        text: centerChallenge.text,
      };
    }

    let challengeIndex = 0;
    for (let i = 1; i <= 25; i++) {
      if (
        i !== CENTER_CHALLENGE_ID &&
        challengeIndex < otherChallenges.length
      ) {
        data[i] = {
          id: i,
          text: otherChallenges[challengeIndex].text || "",
        };
        challengeIndex++;
      }
    }

    return data;
  }, [event?.bingo]);

  const getChallengeText = useCallback(
    (challengeId: number): string => {
      return bingoData[challengeId]?.text || `Desafio ${challengeId}`;
    },
    [bingoData]
  );

  const guestId = useMemo(
    () => guest?.id || Math.random().toString(36).substring(2, 10),
    [guest]
  );

  const checkRowCompletion = useCallback((completedNumbers: number[]) => {
    return BINGO_CONFIG.rows.find((row) =>
      row.every((num) => completedNumbers.includes(num))
    );
  }, []);

  const checkColumnCompletion = useCallback((completedNumbers: number[]) => {
    return BINGO_CONFIG.cols.find((col) =>
      col.every((num) => completedNumbers.includes(num))
    );
  }, []);

  const checkFullCardCompletion = useCallback((completedNumbers: number[]) => {
    return BINGO_CONFIG.allNumbers.every((num) =>
      completedNumbers.includes(num)
    );
  }, []);

  const getCompletedNumbers = useCallback(
    (photos: { [key: number]: string }) => {
      return Object.keys(photos)
        .map(Number)
        .filter((num) => photos[num]);
    },
    []
  );

  const sendBingoResult = useCallback(
    async (
      resultType: "line" | "column" | "fullCard",
      achievedNumbers: number[],
      createdAt: string,
      currentBingoChallengeDone?: TBingoChallengeDone[]
    ) => {
      if (!event || !guest) return;

      try {
        const bingoDataArray = currentBingoChallengeDone || bingoChallengesDone;

        const challengesData = achievedNumbers.map((challengeId) => {
          const challenge = bingoDataArray.find(
            (c) => c.challengeId === challengeId
          );
          return {
            challengeId,
            challengeText: getChallengeText(challengeId),
            photoStoragePath: challenge?.photoStoragePath || "",
            completedAt: challenge?.createdAt || createdAt,
          };
        });

        const bingoResult: TBingoResult = {
          eventId: event.id as string,
          eventCode: event.code,
          guestId,
          guestName: guest.name as string,
          guestPhotoStoragePath: guest.photoStoragePath || null,
          resultType,
          createdAt,
          achievedNumbers,
          challengesData,
          deletedAt: null,
        };

        const cdtAppPublicApi = new CdtAppPublicApi();
        const response = await cdtAppPublicApi.createBingoResult(bingoResult);

        if (response) {
          console.log(`Bingo ${resultType} sent with success:`, response);
        }
      } catch (error) {
        console.error(`Error sending ${resultType} result:`, error);
      }
    },
    [event, guest, guestId, bingoChallengesDone, getChallengeText]
  );

  const checkAndProcessAchievements = useCallback(
    async (
      updatedPhotos: { [key: number]: string },
      challengeId?: number,
      completedAt?: string,
      currentBingoChallengeDone?: TBingoChallengeDone[]
    ) => {
      const completedNumbers = getCompletedNumbers(updatedPhotos);
      const now = new Date();
      const createdAtISO = completedAt || now.toISOString();

      const calculateAchievementTime = (numbers: number[]) => {
        const bingoDataArray = currentBingoChallengeDone || bingoChallengesDone;
        const relevantChallenges = numbers
          .map((num) => bingoDataArray.find((c) => c.challengeId === num))
          .filter(Boolean) as TBingoChallengeDone[];

        if (relevantChallenges.length === numbers.length) {
          const dates = relevantChallenges.map((c) => new Date(c.createdAt));
          return new Date(
            Math.max(...dates.map((d) => d.getTime()))
          ).toISOString();
        }
        return createdAtISO;
      };

      if (!bingoAchievements.firstRow) {
        const completedRow = checkRowCompletion(completedNumbers);
        if (completedRow) {
          const achievementTime = calculateAchievementTime(completedRow);
          const newRowAchievement = {
            numbers: completedRow,
            createdAt: achievementTime,
          };

          setBingoAchievements({
            ...bingoAchievements,
            firstRow: newRowAchievement,
          });

          await sendBingoResult(
            "line",
            completedRow,
            achievementTime,
            currentBingoChallengeDone
          );

          const hasSeenRowCongrats = getGameStatus("bingo_row_congrats_seen");
          if (!hasSeenRowCongrats) {
            setGameStatus("bingo_row_congrats_seen", true);
            setTimeout(() => confettiShoot(), 1000);
          }
        }
      }

      if (!bingoAchievements.firstColumn) {
        const completedColumn = checkColumnCompletion(completedNumbers);
        if (completedColumn) {
          const achievementTime = calculateAchievementTime(completedColumn);
          const newColumnAchievement = {
            numbers: completedColumn,
            createdAt: achievementTime,
          };

          setBingoAchievements({
            ...bingoAchievements,
            firstColumn: newColumnAchievement,
          });

          await sendBingoResult(
            "column",
            completedColumn,
            achievementTime,
            currentBingoChallengeDone
          );

          const hasSeenColumnCongrats = getGameStatus(
            "bingo_column_congrats_seen"
          );
          if (!hasSeenColumnCongrats) {
            setGameStatus("bingo_column_congrats_seen", true);
            setTimeout(() => confettiShoot(), 1000);
          }
        }
      }

      if (!bingoAchievements.fullCard) {
        const isFullCard = checkFullCardCompletion(completedNumbers);
        if (isFullCard) {
          const achievementTime = calculateAchievementTime(
            BINGO_CONFIG.allNumbers
          );
          const newFullCardAchievement = {
            numbers: BINGO_CONFIG.allNumbers,
            createdAt: achievementTime,
          };

          setBingoAchievements({
            ...bingoAchievements,
            fullCard: newFullCardAchievement,
          });

          await sendBingoResult(
            "fullCard",
            BINGO_CONFIG.allNumbers,
            achievementTime,
            currentBingoChallengeDone
          );
          const hasSeenFullCardCongrats = getGameStatus(
            "bingo_fullcard_congrats_seen"
          );
          if (!hasSeenFullCardCongrats) {
            setGameStatus("bingo_fullcard_congrats_seen", true);
            setTimeout(() => confettiShoot(), 1000);
          }
        }
      }
    },
    [
      getCompletedNumbers,
      checkRowCompletion,
      checkColumnCompletion,
      checkFullCardCompletion,
      sendBingoResult,
      bingoChallengesDone,
      bingoAchievements,
      setBingoAchievements,
      getGameStatus,
      setGameStatus,
    ]
  );

  const processPhotoRemoval = useCallback(
    (removedChallengeId: number, currentPhotos: { [key: number]: string }) => {
      const updatedPhotos = { ...currentPhotos };
      delete updatedPhotos[removedChallengeId];

      const completedNumbers = getCompletedNumbers(updatedPhotos);

      const newAchievements = { ...bingoAchievements };

      if (
        bingoAchievements.firstRow &&
        !bingoAchievements.firstRow.numbers.every((num) =>
          completedNumbers.includes(num)
        )
      ) {
        newAchievements.firstRow = null;
        const hasSeenRowCongrats = getGameStatus("bingo_row_congrats_seen");
        if (hasSeenRowCongrats) {
          setGameStatus("bingo_row_congrats_seen", false);
        }
      }

      if (
        bingoAchievements.firstColumn &&
        !bingoAchievements.firstColumn.numbers.every((num) =>
          completedNumbers.includes(num)
        )
      ) {
        newAchievements.firstColumn = null;
        const hasSeenColumnCongrats = getGameStatus(
          "bingo_column_congrats_seen"
        );
        if (hasSeenColumnCongrats) {
          setGameStatus("bingo_column_congrats_seen", false);
        }
      }

      if (
        bingoAchievements.fullCard &&
        !checkFullCardCompletion(completedNumbers)
      ) {
        newAchievements.fullCard = null;
        const hasSeenFullCardCongrats = getGameStatus(
          "bingo_fullcard_congrats_seen"
        );
        if (hasSeenFullCardCongrats) {
          setGameStatus("bingo_fullcard_congrats_seen", false);
        }
      }

      setBingoAchievements(newAchievements);

      setTimeout(async () => {
        await checkAndProcessAchievements(
          updatedPhotos,
          removedChallengeId,
          new Date().toISOString(),
          bingoChallengesDone
        );
      }, 100);

      return updatedPhotos;
    },
    [
      getCompletedNumbers,
      checkFullCardCompletion,
      checkAndProcessAchievements,
      bingoChallengesDone,
      bingoAchievements,
      setBingoAchievements,
      getGameStatus,
      setGameStatus,
    ]
  );

  useEffect(() => {
    const initializeBingoData = async () => {
      const results = await Promise.all(
        bingoChallengesDone.map(async (result) => {
          if (result.photoUrl || !result.uploadId) return result;
          const photoUrl = await SpacesService.getFileUrl(
            result.photoStoragePath
          );
          return { ...result, photoUrl };
        })
      );

      const resultsMap = results.reduce((acc, result) => {
        acc[result.challengeId] = result;
        return acc;
      }, {} as Record<number, TBingoChallengeDone>);

      setCompletedChallenges(resultsMap);

      const photoUrls = results.reduce((acc, result) => {
        if (result.photoUrl) {
          acc[result.challengeId] = result.photoUrl;
        }
        return acc;
      }, {} as { [key: number]: string });

      setTakenPhotos(photoUrls);
    };

    initializeBingoData();
  }, [bingoChallengesDone]);

  const handlePhotoInput = useCallback(
    async (challengeId: number, photoFile: File) => {
      if (!event || !guest) return;

      try {
        const completedAt = new Date().toISOString();
        const completedAtSanitized = completedAt
          .split("T")[1]
          .split(".")[0]
          .replace(/:/g, "");
        const photoStoragePath = `bingo-challenges/${event.id}/${guestId}/${challengeId}-${completedAtSanitized}.jpg`;

        const newBingoChallengeDone: TBingoChallengeDone = {
          eventId: event.id as string,
          eventCode: event.code,
          uploadId: "",
          guestId,
          guestName: guest.name as string,
          guestPhotoStoragePath: guest.photoStoragePath || null,
          challengeId,
          challengeText: getChallengeText(challengeId),
          photoStoragePath,
          createdAt: completedAt,
        };

        addBingoChallengeDone(newBingoChallengeDone);

        if (clarity.hasStarted()) {
          clarity.setTag("engagement_level_creator", "true");
        }

        await SpacesService.uploadFile(photoFile, photoStoragePath);

        const cdtAppPublicApi = new CdtAppPublicApi();
        const newUpload = {
          eventId: event.id as string,
          eventCode: event.code,
          fileStoragePath: photoStoragePath,
          fileName: photoFile.name,
          fileSize: photoFile.size,
          fileType: photoFile.type,
          fileExt: "jpg",
          guestId,
          guestName: guest.name as string,
          guestPhotoStoragePath: guest.photoStoragePath || null,
          tags: ["bingo-challenge", challengeId.toString(), "release-now"],
        };

        const uploadResponse = await cdtAppPublicApi.createUpload(newUpload);

        if (!uploadResponse) {
          throw new Error("Upload failed");
        }

        newBingoChallengeDone.uploadId = uploadResponse.id;
        updateBingoChallengeDone(newBingoChallengeDone);

        const photoUrl = await SpacesService.getFileUrl(photoStoragePath);
        const updatedTakenPhotos = {
          ...takenPhotos,
          [challengeId]: photoUrl,
        };
        setTakenPhotos(updatedTakenPhotos);

        const updatedBingoChallengesDone = [
          ...bingoChallengesDone.filter((c) => c.challengeId !== challengeId),
          newBingoChallengeDone,
        ];

        await checkAndProcessAchievements(
          updatedTakenPhotos,
          challengeId,
          completedAt,
          updatedBingoChallengesDone
        );
      } catch (error) {
        console.error("Error handling bingo photo: ", error);
        removeBingoChallengeDone(challengeId);
      }
    },
    [
      event,
      guest,
      guestId,
      getChallengeText,
      addBingoChallengeDone,
      updateBingoChallengeDone,
      removeBingoChallengeDone,
      takenPhotos,
      bingoChallengesDone,
      checkAndProcessAchievements,
    ]
  );

  const onCheckRemovePhoto = () => {
    setShowRemoveAlert(true);
  };

  const onRemovePhoto = () => {
    if (!activeSlot) return;

    removeBingoChallengeDone(activeSlot);

    setCompletedChallenges((prev) => {
      const newState = { ...prev };
      delete newState[activeSlot];
      return newState;
    });

    const updatedPhotos = processPhotoRemoval(activeSlot, takenPhotos);
    setTakenPhotos(updatedPhotos);

    setShowRemoveAlert(false);
  };

  return (
    <>
      <BaseModal
        isOpen={showRemoveAlert}
        onClose={() => setShowRemoveAlert(false)}
      >
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <h3 className="text-base text-center font-medium">
              <FormattedMessage
                id="message.bingo.remove.warning"
                defaultMessage="Ao excluir este desafio, seus resultados serão recalculados."
              />
            </h3>
            <ul className="list-disc pl-4 space-y-2">
              <li>
                <FormattedMessage
                  id="message.bingo.remove.warning.detail1"
                  defaultMessage="Linhas e colunas afetadas pela exclusão serão revisadas."
                />
              </li>
              <li>
                <FormattedMessage
                  id="message.bingo.remove.warning.detail2"
                  defaultMessage="Se houver outras conquistas completas, o horário será atualizado para o momento atual."
                />
              </li>
            </ul>
          </div>
          <div className="flex items-center justify-between w-full">
            <button
              className={`${
                theme.name === "rose" ? theme.buttonSecondary : theme.button
              } w-[40%]`}
              onClick={() => setShowRemoveAlert(false)}
            >
              <FormattedMessage id="button.cancel" defaultMessage="Cancelar" />
            </button>
            <button
              className={`${
                theme.name === "rose" ? theme.button : theme.buttonSecondary
              } w-[40%]`}
              onClick={onRemovePhoto}
            >
              <FormattedMessage id="button.delete" defaultMessage="Excluir" />
            </button>
          </div>
        </div>
      </BaseModal>
      <ChallengeSection
        activeSlot={activeSlot}
        bingoData={bingoData}
        bingoResultsMap={completedChallenges}
        onPhotoInput={handlePhotoInput}
        onCheckRemovePhoto={onCheckRemovePhoto}
      />
      <div className="w-full h-full flex flex-col items-start justify-center gap-10">
        <BingoBoard
          bingoData={bingoData}
          bingoResultsMap={completedChallenges}
          activeSlot={activeSlot}
          setActiveSlot={setActiveSlot}
        />
        <ScoresSection achievements={bingoAchievements} />
      </div>
    </>
  );
};

const BingoBoard = ({
  bingoData,
  bingoResultsMap,
  activeSlot,
  setActiveSlot,
}: {
  bingoData: Record<number, { id: number; text: string }>;
  bingoResultsMap: Record<number, TBingoChallengeDone>;
  activeSlot: number | null;
  setActiveSlot: (slot: number) => void;
}) => {
  const { theme } = useEventStore();

  const centerIcon = useMemo(
    () => (theme.name === "rose" ? logoRedIcon : logoBlackIcon),
    [theme]
  );

  const allSlots = useMemo(() => {
    return Array.from({ length: 25 }, (_, index) => index + 1);
  }, []);

  return (
    <div className="grid grid-cols-5 gap-2 w-full aspect-square">
      {allSlots.map((slotNumber) => {
        if (slotNumber === CENTER_CHALLENGE_ID) {
          return (
            <div
              key={slotNumber}
              className={`w-full h-full rounded-md flex items-center justify-center ${
                theme.box
              } ${theme.name === "rose" && "bg-[#Fff]"} aspect-square`}
            >
              <img
                src={centerIcon}
                alt="logo"
                className="w-[90%] h-[90%] object-contain"
              />
            </div>
          );
        }

        const hasChallenge = Boolean(bingoData[slotNumber]);
        const isCompleted = Boolean(bingoResultsMap[slotNumber]);

        return (
          <div
            onClick={() => hasChallenge && setActiveSlot(slotNumber)}
            key={slotNumber}
            className={`w-full h-full rounded-md flex items-center justify-center leading-6 aspect-square ${
              isCompleted ? theme.box : theme.boxSecondary
            }`}
          >
            <span
              className={`border-2 rounded-md min-h-[90%] min-w-[90%] flex items-center justify-center aspect-square ${
                activeSlot === slotNumber
                  ? isCompleted
                    ? `${
                        theme.name === "rose" ? "border-white" : "border-black"
                      } font-century-gothic-bold`
                    : `${
                        theme.name === `rose`
                          ? `border-[#F76C6F]`
                          : `border-[#fff]`
                      } font-century-gothic-bold`
                  : "border-transparent"
              }`}
            >
              {slotNumber}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChallengeSection = ({
  activeSlot,
  bingoData,
  bingoResultsMap,
  onPhotoInput,
  onCheckRemovePhoto,
}: {
  activeSlot: number | null;
  bingoData: Record<number, { id: number; text: string }>;
  bingoResultsMap: Record<number, TBingoChallengeDone>;
  onPhotoInput: (challengeId: number, photoFile: File) => void;
  onCheckRemovePhoto: () => void;
}) => {
  const { theme } = useEventStore();
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const activeChallenge = useMemo(() => {
    return activeSlot ? bingoData[activeSlot] : null;
  }, [activeSlot, bingoData]);

  const handleClick = () => {
    if (activeSlot && !bingoResultsMap[activeSlot]) {
      hiddenFileInput.current?.click();
    }
  };

  const handleInputFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    const file = e.target.files?.[0];

    if (!file || !activeSlot) return;

    onPhotoInput(activeSlot, file);
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 pb-8">
      {!activeSlot && (
        <h3 className="text-lg font-medium">
          <FormattedMessage
            id="message.bingo.selectChallenge"
            defaultMessage="Clique para ver e realizar o desafio"
          />
        </h3>
      )}
      {activeSlot && (
        <>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            className="hidden"
            ref={hiddenFileInput}
            onChange={handleInputFileChange}
          />
          {bingoResultsMap[activeSlot] ? (
            <div
              className={`flex relative h-48 justify-between items-center w-full gap-2 bg-white rounded-lg p-2`}
            >
              <div className="flex flex-col h-full justify-between">
                <div className="flex justify-start w-full">
                  <span
                    className={`font-century-gothic-bold text-lg text-white ${
                      theme.name === `rose` ? `bg-[#F76C6F]` : `bg-[#000]`
                    } rounded-lg h-10 w-10 flex items-center justify-center grow-0 shrink-0`}
                  >
                    {activeSlot}
                  </span>
                </div>
                <span className="text-base text-left text-gray-600">
                  {activeChallenge?.text}
                </span>
                <button
                  className={`${
                    theme.name === `rose` ? theme.buttonSecondary : theme.button
                  } flex items-center justify-center gap-2 w-full`}
                  onClick={onCheckRemovePhoto}
                >
                  <Trash
                    width={20}
                    height={20}
                    color="none"
                    outline={`${theme.name === `white` ? `#000` : `#F76C6F`}`}
                  />{" "}
                  <span className="text-sm">
                    <FormattedMessage
                      id="button.bingo.removeChallenge"
                      defaultMessage="Remover Desafio"
                    />
                  </span>
                </button>
              </div>
              {bingoResultsMap[activeSlot].photoUrl ? (
                <img
                  src={bingoResultsMap[activeSlot].photoUrl}
                  alt="Foto realizada"
                  className="w-36 h-36 object-cover rounded-lg"
                />
              ) : (
                <div className="p-2 animate-ping min-h-24 min-w-24 flex items-center justify-center grow-0 shrink-0 bg-white">
                  <span className="animate-ping">
                    <CloudUpload
                      width={24}
                      height={24}
                      color="none"
                      outline="#656565"
                    />
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`w-full flex gap-2 min-h-24 ${theme.card} cursor-pointer`}
              onClick={handleClick}
            >
              <div className="w-full flex items-center gap-2 p-2 min-h-24">
                <div className="flex items-center justify-center border border-gray-300 rounded-xl aspect-square h-24 w-24 min-h-24 min-w-24 grow-0 shrink-0">
                  <CameraOn
                    width={36}
                    height={36}
                    color="none"
                    outline="#000000"
                  />
                </div>
                <span className="text-center text-gray-500 grow">
                  {activeChallenge?.text}
                </span>
                <div className="flex items-center grow-0 shrink-0 px-1">
                  <span className="w-6 h-6 border-2 border-gray-300 rounded-xl"></span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ScoresSection = ({
  achievements,
}: {
  achievements: TBingoAchievements;
}) => {
  const { event, theme } = useEventStore();
  const [createPost, setCreatePost] = useState(false);
  const [activeAchievement, setActiveAchievement] = useState<{
    type: "line" | "column" | "fullCard";
    numbers: number[];
    createdAt: string;
  } | null>(null);
  const { addOptimisticPost } = useFeedStore();
  const navigate = useNavigate();

  const handleClosePostCreation = () => {
    setCreatePost(false);
    setActiveAchievement(null);
  };

  const handlePostCreation = (post: TFeedEvent) => {
    if (post && event?.code) {
      addOptimisticPost(event.code, post);
    }
    setCreatePost(false);
    setActiveAchievement(null);
    if (event?.betaFeatures?.includes("feed")) {
      navigate("/feed/v2");
    } else {
      navigate("/options");
    }
  };

  const handleShareAchievement = (
    type: "line" | "column" | "fullCard",
    numbers: number[],
    createdAt: string
  ) => {
    setActiveAchievement({ type, numbers, createdAt });
    setCreatePost(true);
  };

  const getAchievementText = () => {
    if (!activeAchievement) return "";

    const { type, numbers } = activeAchievement;

    switch (type) {
      case "line":
        return `Completei uma linha no bingo! Números: ${numbers.join(
          ", "
        )} 🎯`;
      case "column":
        return `Completei uma coluna no bingo! Números: ${numbers.join(
          ", "
        )} 🎯`;
      case "fullCard":
        return `BINGO! Completei a cartela inteira! 🎉🎯`;
      default:
        return "";
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center gap-2">
      <h3 className="text-lg font-medium">
        <FormattedMessage
          id="subtitle.bingo.results"
          defaultMessage="Resultados"
        />
      </h3>
      <div className="w-full flex flex-col items-center justify-between gap-2">
        <div className="w-full flex items-center justify-between gap-2">
          <h4 className="text-base font-medium">
            <FormattedMessage
              id="label.bingo.firstRow"
              defaultMessage="1ª - Linha"
            />{" "}
            {achievements.firstRow?.numbers.join(", ") || ""}
          </h4>

          <h4 className="text-base font-medium text-right">
            {achievements.firstRow &&
              formatDateTime(achievements.firstRow.createdAt)}
          </h4>
        </div>
        {achievements.firstRow && event?.betaFeatures?.includes("feed") && (
          <button
            className={`px-3 py-1 text-xs ${theme?.buttonSecondary}`}
            onClick={() =>
              handleShareAchievement(
                "line",
                achievements.firstRow!.numbers,
                achievements.firstRow!.createdAt
              )
            }
          >
            <FormattedMessage
              id="button.bingo.share"
              defaultMessage="Compartilhar"
            />
          </button>
        )}
      </div>

      <div className="w-full flex flex-col items-center justify-between gap-2 mt-4">
        <div className="w-full flex items-center justify-between gap-2">
          <h4 className="text-base font-medium">
            <FormattedMessage
              id="label.bingo.firstColumn"
              defaultMessage="1ª - Coluna"
            />{" "}
            {achievements.firstColumn?.numbers.join(", ") || ""}
          </h4>
          <h4 className="text-base font-medium text-right">
            {achievements.firstColumn &&
              formatDateTime(achievements.firstColumn.createdAt)}
          </h4>
        </div>
        {achievements.firstColumn && event?.betaFeatures?.includes("feed") && (
          <button
            className={`px-3 py-1 text-xs ${theme?.buttonSecondary}`}
            onClick={() =>
              handleShareAchievement(
                "column",
                achievements.firstColumn!.numbers,
                achievements.firstColumn!.createdAt
              )
            }
          >
            <FormattedMessage
              id="button.bingo.share"
              defaultMessage="Compartilhar"
            />
          </button>
        )}
      </div>

      <div className="w-full flex flex-col items-center justify-between gap-2">
        <div className="w-full flex items-center justify-between gap-2">
          <h4 className="text-base font-medium">
            {" "}
            <FormattedMessage
              id="label.bingo.fullCard"
              defaultMessage="Cartela Completa"
            />
          </h4>
          <h4 className="text-base font-medium text-right">
            {achievements.fullCard &&
              formatDateTime(achievements.fullCard.createdAt)}
          </h4>
        </div>
        {achievements.fullCard && event?.betaFeatures?.includes("feed") && (
          <button
            className={`px-3 py-1 text-xs ${theme?.buttonSecondary}`}
            onClick={() =>
              handleShareAchievement(
                "fullCard",
                achievements.fullCard!.numbers,
                achievements.fullCard!.createdAt
              )
            }
          >
            <FormattedMessage
              id="button.bingo.share"
              defaultMessage="Compartilhar"
            />
          </button>
        )}
      </div>
      <CreatePost
        createPost={createPost}
        setPost={setCreatePost}
        onClose={handleClosePostCreation}
        onPostCreated={handlePostCreation}
        frameStyle="bingo"
        gameTag="bingo"
        gameResultText={getAchievementText()}
      />
    </div>
  );
};

// Format to dd/mm - hh:mm:ss
const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
